import { z } from 'zod'
import { attachArticleExtrasSchema } from './article-addition'
import { translatableFieldSchema, translationLanguageSchema } from './translation'
import { STYLE_SOURCES, articleSortSchema, styleSourceSchema } from './template'

/**
 * Report issues (CLI-695) — one dated, editable instance of a report: the thing an intelligence
 * associate opens, pulls coverage into, curates header by header, and sends. Distinct from the
 * report's configuration (`report.ts`), which says what a report IS; an issue is what went out on
 * Tuesday.
 *
 * The persisted shape is headers + items only. Nothing here references a topic, a query or a tag:
 * those decide what the pull brings in, and stop mattering the moment it lands. Moving an article
 * between headers changes nothing upstream (2026-08-31 tech sync).
 */

export const issueStatusSchema = z.enum(['draft', 'scheduled', 'sent', 'failed', 'skipped'])
export type IssueStatus = z.infer<typeof issueStatusSchema>

/**
 * Why a `failed` issue did not go out (CLI-741). `send`: the provider refused every attempt, or a
 * send was interrupted and must be checked before it is repeated. `no_recipients`: the schedule
 * fired with nobody to send to. `generate` and `missed` belong to automatic sends — generation
 * gave up, or the send time passed with nothing ready.
 */
/**
 * `unbuildable`: an automatic report with no headers reached its send moment; there was nothing
 * to build. Like `no_recipients`, this is the report's configuration, not an outage — the
 * monitoring policies route those two (and `report.skipped`) to the analysts rather than to
 * engineering (Kevin, 2026-09-30).
 *
 * `blocked`: a send guard refused the scheduled send on purpose — the legacy portal still delivers
 * the report, this environment may not email any of its recipients, or a preview only sends for
 * test accounts. Configuration again, so it goes to the analysts with the other two (CLI-794).
 */
export const issueFailureReasonSchema = z.enum([
  'generate',
  'send',
  'missed',
  'no_recipients',
  'unbuildable',
  'blocked',
])
export type IssueFailureReason = z.infer<typeof issueFailureReasonSchema>

/**
 * Who authored the issue: an analyst (New draft) or the tick, for one configured send moment
 * (`targetSendAt`). An `auto` issue is an ordinary draft in every other way — open it, edit it,
 * cancel its send — but cancelling makes it `skipped` rather than a draft, so the sweep knows a
 * person decided and does not report a missed send.
 */
export const issueOriginSchema = z.enum(['manual', 'auto'])
export type IssueOrigin = z.infer<typeof issueOriginSchema>

/**
 * The source FAMILY an item belongs to — the same six the template styles by, so "X posts look
 * different" means one thing on both screens. It is also what partitions a header into tabs in the
 * editor: content is edited one source at a time, and a moved item lands grouped with its own kind.
 */
export const issueSourceSchema = styleSourceSchema
export type IssueSource = z.infer<typeof issueSourceSchema>

/** Tab order in the editor and article order within a rendered header. */
export const ISSUE_SOURCES: { key: IssueSource; label: string }[] = STYLE_SOURCES

/**
 * Warehouse `data_source` values → source family. The warehouse names the collector; the product
 * speaks in families. Anything unlisted reads as news, which is both the overwhelming majority and
 * the rendering that leaves an unfamiliar collector's article looking like an article.
 */
const DATA_SOURCE_FAMILY: Record<string, IssueSource> = {
  lexis_nexis_firehose: 'news',
  newscatcher_pubsub: 'news',
  MANUAL: 'news',
  x: 'x',
  twitter: 'x',
  linkedin: 'linkedin',
  podchaser: 'podcast',
  tveyes: 'broadcast',
  newsletter: 'newsletter',
}

export function sourceFamily(dataSource: string | null | undefined): IssueSource {
  return (dataSource && DATA_SOURCE_FAMILY[dataSource]) || 'news'
}

/** The collector names a family covers — what a warehouse `data_source IN (...)` filter takes. */
export function rawSourcesForFamily(family: IssueSource): string[] {
  return Object.entries(DATA_SOURCE_FAMILY)
    .filter(([, fam]) => fam === family)
    .map(([raw]) => raw)
}

/**
 * kept — in the issue, renders. removed — soft-deleted, stays visible dimmed with Restore; the
 * single-step undo that is P0 (deletion history is not). shelved — parked on the shelf while the
 * associate decides where it goes; keeps its origin header so "put it back" is one click.
 */
export const issueItemStateSchema = z.enum(['kept', 'removed', 'shelved'])
export type IssueItemState = z.infer<typeof issueItemStateSchema>

export const issueItemAddedViaSchema = z.enum(['pull', 'manual'])

/**
 * What the pull copied out of the warehouse, so a header renders from the app database alone.
 * Snippets are ready-to-render HTML: escaped text with the producer's matched terms wrapped in
 * `<mark>`, the same shape `ReportArticle.snippets` takes. A hand-entered article carries the same
 * shape with what the associate typed (escaped, no highlights).
 */
/** One outlet's copy of a syndicated story: enough to show it, and enough to fetch it. */
export const syndicationMemberSchema = z.object({
  /** Null for a copy the data API named without a hit id — listed in the email, not promotable. */
  hitId: z.uuid().nullable(),
  /** `hits.created_at`, the partition key — a later fetch of this copy names it. */
  hitCreatedAt: z.iso.datetime().nullable(),
  outlet: z.string().max(255).nullable(),
  url: z.string().max(2000),
  publishDate: z.iso.datetime().nullable(),
})
export type SyndicationMember = z.infer<typeof syndicationMemberSchema>

/**
 * What the relevance judge said about an article, when it ran in SHADOW mode.
 *
 * The data API runs an LLM gate per header. With `relevance_enforce` ON it physically drops what
 * the judge rejects and Headline never sees those articles. With it OFF — shadow — every article
 * comes back carrying `relevanceVerdict`, and "no" means the judge WOULD have dropped it. That is
 * the one worth surfacing: it is an opinion an associate can overrule, on an article that is in
 * the report precisely because nobody is enforcing the opinion yet.
 */
export const relevanceVerdictSchema = z.enum(['yes', 'no'])
export type RelevanceVerdict = z.infer<typeof relevanceVerdictSchema>

export const issueItemSnapshotSchema = z.object({
  headline: z.string().max(1000),
  url: z.string().max(2000),
  outlet: z.string().max(255).nullable().default(null),
  publishDate: z.iso.datetime().nullable().default(null),
  author: z.string().max(255).nullable().default(null),
  summary: z.string().max(4000).nullable().default(null),
  snippets: z.array(z.string().max(4000)).max(10).default([]),
  /** The warehouse collector, kept for the badge; `source` on the item is the family. */
  dataSource: z.string().max(40).nullable().default(null),
  language: z.string().max(16).nullable().default(null),
  reach: z.number().int().nullable().default(null),
  /** A social post's @handle, when known — typed in by hand today; the collectors do not carry it. */
  handle: z.string().max(80).nullable().default(null),
  /**
   * The author's audience when the post was collected — X's `author_followers_count`, which the
   * data API sends as `authorFollowersAtCapture`. Null for every other source and for posts
   * collected before the count was kept. Distinct from `reach`, which for a post is views or an
   * engagement sum, never an audience (the legacy "wrong follower counts" lesson, CLI-516).
   */
  followers: z.number().int().nullable().default(null),
  /** The shadow relevance judge's opinion, when one reached us. Null when the judge didn't run,
   *  when it is enforcing (a dropped article never arrives), or on a warehouse-backed pull. */
  relevanceVerdict: relevanceVerdictSchema.nullable().default(null),
  /** The judge's one-sentence reason behind that verdict, when the data API sent one. */
  relevanceReason: z.string().max(1000).nullable().default(null),
  /** The terms this article matched, when the source reported them. The only thing that can be
   *  highlighted in a post, which carries no snippets of its own. */
  terms: z.array(z.string().max(200)).max(20).default([]),
  isOpinion: z.boolean().default(false),
  /** Other outlets that ran the same story in the window — "also ran in N". */
  syndicationCount: z.number().int().nonnegative().default(0),
  /**
   * The warehouse's syndication group, kept so a re-pull can recognise a sibling copy of a story
   * the issue already holds (or removed). One story per issue, whichever outlet's copy landed.
   */
  syndicationGroupId: z.uuid().nullable().default(null),
  /**
   * The other copies of this story the pull saw — who ran it, and the warehouse ref of each, so
   * "Make headline" can promote one to the primary without another search. Capped, because a
   * wire story can run in hundreds of outlets and the card only ever lists a few.
   */
  syndicatedTo: z.array(syndicationMemberSchema).max(50).default([]),
  /**
   * The warehouse's `max_relevancy_score`. Null on the data API path — it answers in relevance
   * order but never sends the score — and on a hand-added article, so the editor's Relevance sort
   * has to check whether anything in the group carries one before it offers itself.
   */
  relevancy: z.number().nullable().default(null),
  /**
   * Where this article sat in the pull that produced it — the index the answer arrived in, after
   * the header's configured sort. It is the only record of that order once an associate starts
   * dragging, which is what "Original order" in the Sort menu restores. Null on a hand-added
   * article (it was never part of a pull) and on anything pulled before this field existed.
   */
  rank: z.number().int().nonnegative().nullable().default(null),
})
export type IssueItemSnapshot = z.infer<typeof issueItemSnapshotSchema>

/**
 * The associate's edits, layered over the snapshot at render. Stored sparse: a key is present only
 * while it overrides. On PATCH, `null` clears a key (back to the snapshot) and `undefined` leaves it.
 * Override snippets are PLAIN text — typed in a textarea — and the renderer escapes them; only the
 * snapshot's snippets carry markup.
 */
/** A syndicated outlet typed in by hand: its name and where its copy lives. */
export const addedCopySchema = z.object({
  outlet: z.string().trim().min(1).max(255),
  url: z.url().max(2000),
})
export type AddedCopy = z.infer<typeof addedCopySchema>

/**
 * Set when the text in the overrides came from the translator rather than a person. It lives in
 * the overrides — not beside them — so it is written and reversed in the SAME event as the text it
 * describes. That is what makes the card's "Translated from Spanish" marker disappear on Undo
 * without a second mechanism that could disagree with the text on screen.
 *
 * `sourceLang` is what DeepL detected, not what the collector tagged: the detection is the one
 * that was actually acted on.
 */
export const translatedProvenanceSchema = z.object({
  sourceLang: z.string().max(16),
  targetLang: translationLanguageSchema,
  /** Which of the fields above the translator wrote. */
  fields: z.array(translatableFieldSchema).min(1),
})
export type TranslatedProvenance = z.infer<typeof translatedProvenanceSchema>

export const issueItemOverridesSchema = z.object({
  headline: z.string().min(1).max(1000).optional(),
  /** The producer's summary, corrected — or replaced by its translation. */
  summary: z.string().max(4000).optional(),
  snippets: z.array(z.string().max(4000)).max(10).optional(),
  isOpinion: z.boolean().optional(),
  // The byline: what an associate corrects most after the headline. A wrong outlet or author from
  // a collector must not reach a client, and the snapshot stays as pulled so a reset is honest.
  outlet: z.string().min(1).max(255).optional(),
  author: z.string().min(1).max(255).optional(),
  url: z.url().max(2000).optional(),
  publishDate: z.iso.datetime().optional(),
  /** Views or followers reached, corrected — a count the collector missed or got wrong. */
  reach: z.number().int().nonnegative().max(1_000_000_000).optional(),
  /**
   * Syndication, corrected. `hiddenCopies` names (by URL) copies from the pull that should not
   * show; `addedCopies` are outlets typed in by hand. Both are overrides like any other, so
   * removing or adding a syndicated outlet rides the edit trail and is undone the same way a
   * headline edit is — the gap the demo named (2026-09-11).
   */
  hiddenCopies: z.array(z.string().max(2000)).max(50).optional(),
  addedCopies: z.array(addedCopySchema).max(25).optional(),
  translated: translatedProvenanceSchema.optional(),
})
export type IssueItemOverrides = z.infer<typeof issueItemOverridesSchema>

export const issueItemOverridesPatchSchema = z.object({
  headline: z.string().min(1).max(1000).nullable().optional(),
  summary: z.string().max(4000).nullable().optional(),
  snippets: z.array(z.string().max(4000)).max(10).nullable().optional(),
  isOpinion: z.boolean().nullable().optional(),
  outlet: z.string().min(1).max(255).nullable().optional(),
  author: z.string().min(1).max(255).nullable().optional(),
  url: z.url().max(2000).nullable().optional(),
  publishDate: z.iso.datetime().nullable().optional(),
  reach: z.number().int().nonnegative().max(1_000_000_000).nullable().optional(),
  hiddenCopies: z.array(z.string().max(2000)).max(50).nullable().optional(),
  addedCopies: z.array(addedCopySchema).max(25).nullable().optional(),
  translated: translatedProvenanceSchema.nullable().optional(),
})
export type IssueItemOverridesPatch = z.infer<typeof issueItemOverridesPatchSchema>

/**
 * The copies of a story as the associate has left them: what the pull found, less the hidden
 * ones, plus the ones typed in. One function so the editor's list and the email's "Syndicated"
 * line agree about which outlets ran it.
 */
export function effectiveCopies(
  snapshot: Pick<IssueItemSnapshot, 'syndicatedTo'>,
  overrides: Pick<IssueItemOverrides, 'hiddenCopies' | 'addedCopies'>,
): SyndicationMember[] {
  const hidden = new Set(overrides.hiddenCopies ?? [])
  const kept = snapshot.syndicatedTo.filter((copy) => !hidden.has(copy.url))
  const added: SyndicationMember[] = (overrides.addedCopies ?? []).map((copy) => ({
    hitId: null,
    hitCreatedAt: null,
    outlet: copy.outlet,
    url: copy.url,
    publishDate: null,
  }))
  return [...kept, ...added]
}

export const reportIssueItemSchema = z.object({
  id: z.uuid(),
  issueId: z.uuid(),
  sectionId: z.uuid(),
  /** Null for a hand-entered article — the warehouse is read-only, so it lives only here. */
  hitId: z.uuid().nullable(),
  /**
   * `hits.created_at`, carried so every later warehouse read (snippets, full text) can name the
   * partition. A lookup by hit id alone scans ~600 daily partitions.
   */
  hitCreatedAt: z.iso.datetime().nullable(),
  source: issueSourceSchema,
  position: z.number().int().nonnegative(),
  state: issueItemStateSchema,
  snapshot: issueItemSnapshotSchema,
  overrides: issueItemOverridesSchema,
  movedFromSectionId: z.uuid().nullable(),
  addedVia: issueItemAddedViaSchema,
  createdAt: z.iso.datetime(),
  updatedAt: z.iso.datetime(),
})
export type ReportIssueItem = z.infer<typeof reportIssueItemSchema>

export const issueSourceCountSchema = z.object({
  source: issueSourceSchema,
  kept: z.number().int().nonnegative(),
  removed: z.number().int().nonnegative(),
})
export type IssueSourceCount = z.infer<typeof issueSourceCountSchema>

/**
 * A header as this issue knows it — a snapshot of the report's section taken when the issue was
 * created, so renaming or deleting a header in the config later doesn't rewrite what was sent.
 * `sectionId` is the live section while it exists; per-header presentation is read through it.
 */
export const reportIssueSectionSchema = z.object({
  id: z.uuid(),
  issueId: z.uuid(),
  sectionId: z.uuid().nullable(),
  name: z.string(),
  /** The configuration's subtitle for this header, read live like the name. Empty = none. */
  subtitle: z.string().default(''),
  position: z.number().int().nonnegative(),
  counts: z.array(issueSourceCountSchema),
  /** When an associate marked this header done; null until they do, and again if they unmark it. */
  reviewedAt: z.iso.datetime().nullable(),
  /**
   * The sort the report configuration gives this header — its own presentation's, else the
   * template's default. The pull seeds the header in this order; after that the editor's order
   * is the order, and this only tells the Sort menu which entry is the configured one.
   */
  sort: articleSortSchema.default('ranked'),
  /** The header's outlet order, resolved: the priority list's members, or the ad-hoc list. */
  outletPriority: z.array(z.string()).default([]),
})
export type ReportIssueSection = z.infer<typeof reportIssueSectionSchema>

export const reportIssueSchema = z.object({
  id: z.uuid(),
  reportId: z.uuid(),
  subaccountId: z.uuid(),
  /** Calendar date in the report's timezone, YYYY-MM-DD. */
  issueDate: z.iso.date(),
  status: issueStatusSchema,
  /** The publish-date window the pull covers. */
  windowFrom: z.iso.datetime(),
  windowTo: z.iso.datetime(),
  pulledAt: z.iso.datetime().nullable(),
  sentAt: z.iso.datetime().nullable(),
  /** Set while status is `scheduled`: the moment the configured recipients get it. */
  scheduledFor: z.iso.datetime().nullable(),
  origin: issueOriginSchema,
  /** The configured send moment an automatic issue exists for; null for a manual draft. */
  targetSendAt: z.iso.datetime().nullable(),
  /** Set while status is `failed`: when, why, and what the provider last said. */
  failedAt: z.iso.datetime().nullable(),
  failureReason: issueFailureReasonSchema.nullable(),
  lastError: z.string().nullable(),
  createdAt: z.iso.datetime(),
  updatedAt: z.iso.datetime(),
  sections: z.array(reportIssueSectionSchema),
  shelfCount: z.number().int().nonnegative(),
})
export type ReportIssue = z.infer<typeof reportIssueSchema>

export const reportIssueSummarySchema = reportIssueSchema
  .omit({ sections: true, shelfCount: true })
  .extend({
    sectionCount: z.number().int().nonnegative(),
    /** Kept items across every header. */
    itemCount: z.number().int().nonnegative(),
    /** When each real send went out, oldest first — "Send again" adds one; `sentAt` is the latest. */
    sentAts: z.array(z.iso.datetime()),
  })
export type ReportIssueSummary = z.infer<typeof reportIssueSummarySchema>

export const listIssuesQueryStringSchema = z.object({ reportId: z.uuid() })
export const listIssuesResponseSchema = z.object({ issues: z.array(reportIssueSummarySchema) })
export type ListIssuesResponse = z.infer<typeof listIssuesResponseSchema>

/** A window is at most 31 days — each day is a warehouse partition. */
const MAX_WINDOW_MS = 31 * 24 * 60 * 60 * 1000

/** `from` before `to`, and no more than `MAX_WINDOW_MS` apart. */
const withinWindowBounds = (from: string, to: string) =>
  new Date(from) < new Date(to) &&
  new Date(to).getTime() - new Date(from).getTime() <= MAX_WINDOW_MS

export const createIssueSchema = z
  .object({
    reportId: z.uuid(),
    /** Defaults to today in the report's timezone. */
    issueDate: z.iso.date().optional(),
    /** Both or neither. Defaults to the 24 hours ending now. */
    windowFrom: z.iso.datetime().optional(),
    windowTo: z.iso.datetime().optional(),
  })
  .refine((v) => (v.windowFrom === undefined) === (v.windowTo === undefined), {
    message: 'windowFrom and windowTo go together.',
  })
  .refine(
    (v) =>
      v.windowFrom === undefined ||
      v.windowTo === undefined ||
      withinWindowBounds(v.windowFrom, v.windowTo),
    { message: 'windowFrom must be before windowTo, and the window at most 31 days.' },
  )
export type CreateIssue = z.infer<typeof createIssueSchema>

/**
 * Re-aim the pull: a Friday report covering the long weekend, a re-run over one busy afternoon.
 * The same bounds as creation, and the items already pulled stay — a new window only changes what
 * the next pull looks at.
 */
export const updateIssueSchema = z
  .object({
    windowFrom: z.iso.datetime(),
    windowTo: z.iso.datetime(),
  })
  .refine((v) => withinWindowBounds(v.windowFrom, v.windowTo), {
    message: 'windowFrom must be before windowTo, and the window at most 31 days.',
  })
export type UpdateIssue = z.infer<typeof updateIssueSchema>

/**
 * The engineering preview's optional replay window (CLI-625; Kevin, 2026-10-08: "pull the same
 * times as this morning's report so we compare from the time it sent yesterday until the time it
 * sent this morning"). Query parameters of `GET /engineering/reports/:id/preview` — both or
 * neither, from before to, at most the 31 days an issue's window may span, ISO datetimes with
 * `Z` or an offset. Named, the preview pulls THAT window instead of New draft's default, judges
 * the previously-sent exclusion as of `sentAsOf` — the previous send's moment; it defaults to
 * `windowFrom` and must lie within the window — and renders the subject's date for `windowTo`.
 * The two are separate because the default window now opens a day BEFORE the previous send
 * (`LOOKBACK_MS`, the overlap legacy's engine reads), so a faithful replay pulls from `previous
 * send − 24 h` while the exclusion is still judged as of the previous send. The session route
 * takes no such parameters.
 */
export const previewReplayQuerySchema = z
  .object({
    windowFrom: z.iso.datetime({ offset: true }).optional(),
    windowTo: z.iso.datetime({ offset: true }).optional(),
    sentAsOf: z.iso.datetime({ offset: true }).optional(),
  })
  .refine((v) => (v.windowFrom === undefined) === (v.windowTo === undefined), {
    message: 'windowFrom and windowTo go together.',
  })
  .refine(
    (v) =>
      v.windowFrom === undefined ||
      v.windowTo === undefined ||
      withinWindowBounds(v.windowFrom, v.windowTo),
    { message: 'windowFrom must be before windowTo, and the window at most 31 days.' },
  )
  .refine((v) => v.sentAsOf === undefined || v.windowFrom !== undefined, {
    message: 'sentAsOf needs a replay window (windowFrom and windowTo).',
  })
  .refine(
    (v) =>
      v.sentAsOf === undefined ||
      v.windowFrom === undefined ||
      v.windowTo === undefined ||
      withinReplayWindow(v.sentAsOf, v.windowFrom, v.windowTo),
    { message: 'sentAsOf must be between windowFrom and windowTo, inclusive.' },
  )
export type PreviewReplayQuery = z.infer<typeof previewReplayQuerySchema>

/** `windowFrom <= sentAsOf <= windowTo`, as instants. */
function withinReplayWindow(sentAsOf: string, windowFrom: string, windowTo: string): boolean {
  const at = new Date(sentAsOf).getTime()
  return new Date(windowFrom).getTime() <= at && at <= new Date(windowTo).getTime()
}

/** Marking a header reviewed is a fact about the associate's pass, not about the articles. */
export const updateIssueSectionSchema = z.object({ reviewed: z.boolean() })
export type UpdateIssueSection = z.infer<typeof updateIssueSectionSchema>

/** Promote one of the story's other copies to the primary; the current primary joins the list. */
export const makeHeadlineSchema = z.object({ hitId: z.uuid() })
export type MakeHeadline = z.infer<typeof makeHeadlineSchema>

/** Send to the configured recipients at a moment; the editor stays open until then. */
export const scheduleIssueSchema = z.object({ sendAt: z.iso.datetime() })
export type ScheduleIssue = z.infer<typeof scheduleIssueSchema>

export const listIssueItemsQueryStringSchema = z.object({
  /** One header at a time — the editor never loads an issue's items wholesale. */
  sectionId: z.uuid().optional(),
  source: issueSourceSchema.optional(),
  /** '1' = the shelf: shelved items across every header. Ignores sectionId. */
  shelf: z.enum(['1']).optional(),
  limit: z.coerce.number().int().min(1).max(500).default(200),
  offset: z.coerce.number().int().min(0).default(0),
})
export const issueItemsPageSchema = z.object({
  items: z.array(reportIssueItemSchema),
  hasMore: z.boolean(),
})
export type IssueItemsPage = z.infer<typeof issueItemsPageSchema>

export const updateIssueItemSchema = z
  .object({
    /** kept ↔ removed ↔ shelved. Shelving keeps the header; restoring puts it back there. */
    state: issueItemStateSchema.optional(),
    overrides: issueItemOverridesPatchSchema.optional(),
  })
  .refine((body) => Object.keys(body).length > 0, { message: 'Nothing to update.' })
export type UpdateIssueItem = z.infer<typeof updateIssueItemSchema>

export const moveIssueItemSchema = z.object({
  toSectionId: z.uuid(),
  /** Index within the target header's same-source group. Omitted = the end of that group. */
  position: z.number().int().nonnegative().optional(),
})
export type MoveIssueItem = z.infer<typeof moveIssueItemSchema>

/** One header, one source, the complete new order. Ids the group doesn't hold are ignored. */
export const reorderIssueItemsSchema = z.object({
  source: issueSourceSchema,
  itemIds: z.array(z.uuid()).min(1).max(1000),
})
export type ReorderIssueItems = z.infer<typeof reorderIssueItemsSchema>

export const addIssueItemSchema = z.discriminatedUnion('kind', [
  /** A warehouse hit the associate found through search — snapshot taken server-side. */
  z.object({
    kind: z.literal('hit'),
    sectionId: z.uuid(),
    hitId: z.uuid(),
    hitCreatedAt: z.iso.datetime(),
  }),
  /**
   * Typed in by hand: coverage the pipeline never saw. A news article, a post on X or an episode
   * is attached to the warehouse first (lib/issue-attach) and lives there; the other sources live
   * only in the issue. The source is required — an article is news, or a post, or a segment, never
   * "all of them" — and the fields are the attach's own (`attachArticleExtrasSchema`); which of
   * them a source asks for is `MANUAL_SHAPES`, the one catalogue every add-by-hand screen reads.
   */
  attachArticleExtrasSchema.extend({
    kind: z.literal('manual'),
    sectionId: z.uuid(),
    source: issueSourceSchema,
    /** The headline of an article, the text of a post, the title of a segment or episode. */
    headline: z.string().trim().min(1).max(1000),
    url: z.url().max(2000),
  }),
])
export type AddIssueItem = z.infer<typeof addIssueItemSchema>

/**
 * Search is scoped to what the report's own topics matched in the window — coverage the pull's
 * limits cut off — plus an exact URL lookup. A free-text search of the whole warehouse is a
 * full-text index away and not this endpoint's job.
 */
/** Find in report: the draft's own articles that mention a term, with the header each sits in. */
export const findInIssueQueryStringSchema = z.object({
  q: z.string().trim().min(2).max(300),
  limit: z.coerce.number().int().min(1).max(100).default(40),
})
export const issueFindMatchSchema = z.object({
  itemId: z.uuid(),
  sectionId: z.uuid(),
  sectionName: z.string(),
  source: issueSourceSchema,
  state: issueItemStateSchema,
  headline: z.string(),
  outlet: z.string().nullable(),
  /** Which field the term was found in, and the text around it. */
  matchedIn: z.enum(['headline', 'outlet', 'author', 'summary', 'snippet']),
  excerpt: z.string(),
})
export type IssueFindMatch = z.infer<typeof issueFindMatchSchema>
export const findInIssueResponseSchema = z.object({ matches: z.array(issueFindMatchSchema) })
export type FindInIssueResponse = z.infer<typeof findInIssueResponseSchema>

export const searchHitsQueryStringSchema = z.object({
  q: z.string().trim().min(2).max(300),
  source: issueSourceSchema.optional(),
  limit: z.coerce.number().int().min(1).max(50).default(25),
})
export const hitSearchResultSchema = z.object({
  hitId: z.uuid(),
  hitCreatedAt: z.iso.datetime(),
  headline: z.string(),
  url: z.string(),
  outlet: z.string().nullable(),
  publishDate: z.iso.datetime().nullable(),
  author: z.string().nullable(),
  source: issueSourceSchema,
  dataSource: z.string().nullable(),
  /** Already an item of this issue (any state) — the dialog shows it disabled. */
  alreadyInIssue: z.boolean(),
})
export type HitSearchResult = z.infer<typeof hitSearchResultSchema>
export const searchHitsResponseSchema = z.object({ hits: z.array(hitSearchResultSchema) })
export type SearchHitsResponse = z.infer<typeof searchHitsResponseSchema>

/** Fetched on expand, never with the list: the producer's snippets and the licensed full text. */
export const issueItemDetailSchema = z.object({
  snippets: z.array(z.object({ text: z.string(), position: z.number().int().nullable() })),
  content: z.string().nullable(),
})
export type IssueItemDetail = z.infer<typeof issueItemDetailSchema>

export const pullIssueResponseSchema = z.object({
  added: z.number().int().nonnegative(),
  /** Already in the issue, in any state — a re-pull never resurrects what was removed. */
  skipped: z.number().int().nonnegative(),
  sections: z.array(
    z.object({ sectionId: z.uuid(), name: z.string(), added: z.number().int().nonnegative() }),
  ),
  /**
   * Headers whose content could not be fetched, one entry per header and source. A pull asks the
   * data API once per header per source, and one of those calls failing used to throw away the
   * whole issue — thirty headers that answered fine included. They are reported instead, so the
   * issue keeps what it got and the editor can say which headers to pull again.
   */
  failed: z.array(
    z.object({
      sectionId: z.uuid(),
      name: z.string(),
      source: issueSourceSchema,
      /** What went wrong, for the editor to show. Never a stack or a query. */
      reason: z.string(),
    }),
  ),
})
export type PullIssueResponse = z.infer<typeof pullIssueResponseSchema>

export const issuePreviewResponseSchema = z.object({ html: z.string() })
export type IssuePreviewResponse = z.infer<typeof issuePreviewResponseSchema>

/**
 * What a report would send right now, without sending it (CLI-625): the editor's pull and the
 * mailer's render run for an issue that is never committed — `previewReport` in
 * apps/api/src/lib/issue-preview.ts builds it with `persist: false`, so nothing is written. Served
 * to the editor (`GET /reports/:id/preview`, the session) and to engineering
 * (`GET /engineering/reports/:id/preview`, its own token) in one shape, so the parity tool reads
 * the same fields the editor's dialog shows. Recipients are counts and domains only, on both
 * routes: the engineering caller has no session, and the editor sees the addresses on the
 * Delivery tab already.
 */
export const reportPreviewResponseSchema = z.object({
  reportId: z.uuid(),
  name: z.string(),
  /** When the pull ran — the preview's "as of". */
  pulledAt: z.iso.datetime(),
  /**
   * The coverage window the pull covered: New draft's default, ending at `pulledAt` — or, on a
   * replay, the window the caller named.
   */
  window: z.object({ from: z.iso.datetime(), to: z.iso.datetime() }),
  /**
   * True when the caller named the window (`previewReplayQuerySchema`): a replay of the send at
   * `window.to`, previously-sent judged as of `window.from` — not what would send right now.
   */
  replay: z.boolean(),
  /** The subject as the mailer would send it — legacy date tokens rendered in the report's timezone. */
  subject: z.string(),
  html: z.string(),
  template: z.enum(['standard', 'custom']),
  templateId: z.uuid().nullable(),
  from: z.object({ email: z.string(), name: z.string() }),
  recipients: z.object({
    to: z.number().int().nonnegative(),
    cc: z.number().int().nonnegative(),
    bcc: z.number().int().nonnegative(),
    domains: z.array(z.string()),
  }),
  pull: z.object({
    added: z.number().int().nonnegative(),
    skipped: z.number().int().nonnegative(),
    failed: pullIssueResponseSchema.shape.failed,
    durationMs: z.number().int().nonnegative(),
  }),
  sections: z.array(
    z.object({
      /** The live header's id; null for a header the configuration no longer has. */
      sectionId: z.uuid().nullable(),
      name: z.string(),
      counts: z.array(
        z.object({ source: issueSourceSchema, count: z.number().int().nonnegative() }),
      ),
      /** The kept articles in issue order — what the email lists under this header. */
      articles: z.array(
        z.object({
          title: z.string().nullable(),
          url: z.string().nullable(),
          outlet: z.string().nullable(),
          hitId: z.string().nullable(),
          source: issueSourceSchema,
          date: z.string().nullable(),
        }),
      ),
    }),
  ),
})
export type ReportPreviewResponse = z.infer<typeof reportPreviewResponseSchema>

/**
 * SendGrid's ceiling: at most 1,000 recipients per request, counting to, cc and bcc TOGETHER —
 * not per list. A per-list cap of 100 used to refuse a client whose bcc alone had 128 people.
 */
export const MAX_EMAIL_RECIPIENTS = 1000

const emailAddress = z.email()

/** The same test the API's `z.email()` applies — so the UI can flag what a send would refuse. */
export function isEmailAddress(value: string): boolean {
  return emailAddress.safeParse(value).success
}

/** Distinct addresses across lists, counted the way the mailer dedupes them (case-insensitively). */
export function countDistinctRecipients(...lists: Array<readonly string[] | undefined>): number {
  const seen = new Set<string>()
  for (const list of lists) for (const address of list ?? []) seen.add(address.trim().toLowerCase())
  return seen.size
}

export const issueSendModeSchema = z.enum(['send', 'test'])
export const sendIssueSchema = z
  .object({
    /** send = the configured recipients (or the override below); test = only `to`, nothing else. */
    mode: issueSendModeSchema,
    to: z.array(emailAddress).optional(),
    cc: z.array(emailAddress).optional(),
    bcc: z.array(emailAddress).optional(),
    subject: z.string().trim().min(1).max(200).optional(),
  })
  // Distinct, as the mailer sends and SendGrid counts — one person in both To and Bcc is one.
  .refine((send) => countDistinctRecipients(send.to, send.cc, send.bcc) <= MAX_EMAIL_RECIPIENTS, {
    message: `A send can reach at most ${MAX_EMAIL_RECIPIENTS} recipients across to, cc and bcc.`,
  })
export type SendIssue = z.infer<typeof sendIssueSchema>

export const reportIssueSendSchema = z.object({
  id: z.uuid(),
  issueId: z.uuid(),
  mode: issueSendModeSchema,
  recipients: z.object({
    to: z.array(z.string()),
    cc: z.array(z.string()),
    bcc: z.array(z.string()),
    /** The global report Bcc the send carried (CLI-850) — present for staff readers only. */
    globalBcc: z.array(z.string()).optional(),
  }),
  subject: z.string(),
  sentBy: z.uuid(),
  sentAt: z.iso.datetime(),
  providerMessageId: z.string().nullable(),
  /** `sending` is a claim taken before the provider was called; see the table comment. */
  status: z.enum(['sending', 'sent', 'failed']),
  error: z.string().nullable(),
})
export type ReportIssueSend = z.infer<typeof reportIssueSendSchema>
export const listIssueSendsResponseSchema = z.object({ sends: z.array(reportIssueSendSchema) })
export type ListIssueSendsResponse = z.infer<typeof listIssueSendsResponseSchema>

/**
 * The edit trail (CLI-695). Every mutation the editor makes is one row, and the ones that record
 * what they replaced can be undone — see `apps/api/src/lib/issue-undo.ts`. `undoable` is decided
 * server-side (the action is reversible, nobody has undone it yet) so the client never guesses.
 */
export const UNDOABLE_ISSUE_ACTIONS = [
  'item.edit',
  'item.remove',
  'item.shelve',
  'item.restore',
  'item.move',
  'item.add',
  'item.make_headline',
  'item.translate',
  'section.reorder',
  'section.reviewed',
  'section.unreviewed',
  'issue.window',
] as const
export type UndoableIssueAction = (typeof UNDOABLE_ISSUE_ACTIONS)[number]

export function isUndoableIssueAction(action: string): action is UndoableIssueAction {
  return (UNDOABLE_ISSUE_ACTIONS as readonly string[]).includes(action)
}

export const issueEventSchema = z.object({
  id: z.uuid(),
  action: z.string(),
  /** What the action recorded — shape depends on the action; the client reads it defensively. */
  payload: z.record(z.string(), z.unknown()),
  actorId: z.uuid(),
  /** Display name or email of who did it; null when the user row is gone. */
  actorName: z.string().nullable(),
  createdAt: z.iso.datetime(),
  undoneAt: z.iso.datetime().nullable(),
  undoable: z.boolean(),
})
export type IssueEvent = z.infer<typeof issueEventSchema>

export const listIssueEventsResponseSchema = z.object({ events: z.array(issueEventSchema) })
export type ListIssueEventsResponse = z.infer<typeof listIssueEventsResponseSchema>
